import { useCallback, useEffect, useState } from 'react';
import {
  listQueue,
  remove,
  onConnectivityRestore,
  isOnline,
  clearDone,
} from '../lib/submissionQueue.js';
import { drainOnce } from '../lib/submissionDrain.js';

// Surface the offline submission queue alongside the live Wilman feed.
// Players need to see (a) what's pending, (b) any drain failures, and
// (c) a manual "drain now" button when the auto-drain doesn't catch.

export default function QueuedSubmissions() {
  const [items, setItems] = useState([]);
  const [draining, setDraining] = useState(false);
  const [online, setOnline] = useState(isOnline());

  const refresh = useCallback(async () => {
    try {
      const list = await listQueue();
      setItems(list);
    } catch {
      setItems([]);
    }
  }, []);

  useEffect(() => {
    refresh();
    const offReconnect = onConnectivityRestore(async () => {
      setOnline(true);
      // Auto-drain on reconnect — best-effort, ignore failures.
      try {
        await drainOnce();
      } finally {
        await refresh();
      }
    });
    const onOff = () => setOnline(false);
    window.addEventListener('offline', onOff);
    // The SW posts {kind: 'drain-queue'} when Background Sync fires.
    const onMsg = async (e) => {
      if (e.data?.kind === 'drain-queue') {
        try {
          await drainOnce();
        } finally {
          await refresh();
        }
      }
    };
    if (typeof navigator !== 'undefined' && navigator.serviceWorker) {
      navigator.serviceWorker.addEventListener('message', onMsg);
    }
    return () => {
      offReconnect();
      window.removeEventListener('offline', onOff);
      if (typeof navigator !== 'undefined' && navigator.serviceWorker) {
        navigator.serviceWorker.removeEventListener('message', onMsg);
      }
    };
  }, [refresh]);

  const drainNow = useCallback(async () => {
    setDraining(true);
    try {
      await drainOnce();
      await refresh();
    } finally {
      setDraining(false);
    }
  }, [refresh]);

  const removeOne = useCallback(
    async (id) => {
      await remove(id);
      await refresh();
    },
    [refresh]
  );

  const onClearDone = useCallback(async () => {
    await clearDone();
    await refresh();
  }, [refresh]);

  if (items.length === 0) return null;

  const pending = items.filter((i) => i.status !== 'done' && i.status !== 'failed');
  const done = items.filter((i) => i.status === 'done');
  const failed = items.filter((i) => i.status === 'failed');

  return (
    <section className="card overflow-hidden border-copper/40">
      <header className="flex items-baseline justify-between gap-2 border-b border-copper/40 bg-copper/10 px-4 py-3">
        <div>
          <p className="font-mono text-[10px] uppercase tracking-wider text-copper-dark">
            offline queue · {pending.length} pending
            {failed.length > 0 ? ` · ${failed.length} failed` : ''}
          </p>
          <p className="text-sm text-fg-primary">
            {online
              ? draining
                ? 'Draining now…'
                : pending.length > 0
                  ? 'Connected — tap "drain now" to push.'
                  : 'All synced.'
              : 'Offline. Held until signal returns.'}
          </p>
        </div>
        <div className="flex gap-1.5">
          {pending.length > 0 && (
            <button
              type="button"
              onClick={drainNow}
              disabled={draining || !online}
              className="rounded-md border border-copper bg-copper px-2.5 py-1 font-mono text-[10px] uppercase tracking-wider text-action-fg transition disabled:opacity-50"
            >
              {draining ? '…' : 'drain now'}
            </button>
          )}
          {done.length > 0 && (
            <button
              type="button"
              onClick={onClearDone}
              className="rounded-md border border-border-tertiary bg-bg-secondary px-2.5 py-1 font-mono text-[10px] uppercase tracking-wider text-fg-secondary transition hover:bg-bg-tertiary"
            >
              clear done
            </button>
          )}
        </div>
      </header>
      <ul className="divide-y divide-border-tertiary">
        {items.map((item) => (
          <li key={item.id} className="flex items-baseline justify-between gap-2 px-4 py-2">
            <div className="min-w-0">
              <p className="font-mono text-[10px] uppercase tracking-wider text-fg-tertiary">
                {item.challenge_id} · {item.submission_type}
              </p>
              <p className="font-mono text-[11px] text-fg-secondary">
                {prettyStatus(item)}
                {item.last_error && (
                  <span className="ml-1 text-danger"> · {item.last_error}</span>
                )}
              </p>
            </div>
            <button
              type="button"
              onClick={() => removeOne(item.id)}
              className="font-mono text-[10px] uppercase tracking-wider text-fg-tertiary underline-offset-4 hover:underline"
            >
              remove
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}

function prettyStatus(item) {
  if (item.status === 'queued') return `queued · attempt ${item.attempts || 0}`;
  if (item.status === 'uploading') return 'uploading…';
  if (item.status === 'verifying') return 'awaiting verdict…';
  if (item.status === 'done') {
    const v = item.result?.submission;
    if (!v) return 'sent';
    return `${v.verified ? 'verified' : 'rejected'} · ${v.points >= 0 ? '+' : ''}${v.points} pts`;
  }
  if (item.status === 'failed') return `failed after ${item.attempts || 0} attempts`;
  return item.status;
}
